import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Phone, MapPin, Clock, ArrowRight, ArrowUpRight, ShieldCheck, HeartHandshake, Accessibility, Star } from "lucide-react";
import { CLINIC, SERVICES, TESTIMONIALS, IMAGES, GOOGLE_REVIEW } from "../data/clinic";
import { FadeUp, MaskedLine, SectionTag, Stars } from "../components/Reveal";
import Marquee from "../components/Marquee";
import storefront1 from "../assets/storefront-night.jpg";
import storefront2 from "../assets/eyewear-wall.jpg";
import storefront3 from "../assets/storefront-3.jpeg";
import optiplusLogo from "../assets/optiplus-logo.png";

const Hero = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 45, damping: 18 });
    const sy = useSpring(my, { stiffness: 45, damping: 18 });
    const imgX = useTransform(sx, [-0.5, 0.5], [26, -26]);
    const imgY = useTransform(sy, [-0.5, 0.5], [18, -18]);
    const ringX = useTransform(sx, [-0.5, 0.5], [-40, 40]);
    const ringY = useTransform(sy, [-0.5, 0.5], [-26, 26]);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
    const drift = useTransform(scrollYProgress, [0, 1], [0, 120]);

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMove}
            className="relative flex min-h-screen items-center overflow-hidden"
            data-testid="home-hero"
        >
            <motion.div style={{ x: imgX, y: imgY }} className="absolute inset-[-4%]">
                <img
                    src={IMAGES.heroEye}
                    alt="Macro human eye iris"
                    className="emerald-duotone h-full w-full object-cover opacity-40"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#060a07]/80 via-[#060a07]/55 to-[#060a07]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#060a07]/85 via-transparent to-[#060a07]/40" />
            <div className="noise-overlay pointer-events-none absolute inset-0 opacity-30" />

            <motion.div
                style={{ x: ringX, y: ringY }}
                className="pointer-events-none absolute right-[8%] top-[18%] hidden lg:block"
            >
                <div className="animate-spin-slow h-56 w-56 rounded-full border border-emerald-500/25 shadow-[0_0_80px_rgba(34,197,94,0.12)]" />
                <div className="absolute inset-6 rounded-full border border-emerald-400/15" />
            </motion.div>

            <motion.div
                style={{ opacity: fade, y: drift }}
                className="relative mx-auto w-full max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16"
            >
                <MaskedLine delay={0.3}>
                    <span className="mb-6 inline-flex items-center gap-4 rounded-full border border-emerald-500/30 bg-emerald-950/60 px-8 py-4 text-base font-mono tracking-[0.25em] uppercase text-emerald-300 backdrop-blur-md">
  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(74,222,128,0.9)]" />
  If you can't see, see us
</span>
                </MaskedLine>

                <MaskedLine delay={0.42} className="mb-8 max-w-md">
                    <p className="text-sm font-light leading-relaxed text-[#a3b8a8]">
                        <img
                            src={optiplusLogo}
                            alt="Optiplus logo"
                            className="mb-2 h-16 w-auto brightness-[1.35]"
                        />
                        1/69, Naktala, (Beside Shakti Sangha Club)
                        <br />
                        Kolkata - 700047, West Bengal, India.
                    </p>
                </MaskedLine>

                <h1
                    data-testid="hero-heading"
                    className="font-display max-w-4xl text-3xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-[4rem]"
                >
                    <MaskedLine delay={0.5}>Vision, restored.</MaskedLine>
                    <MaskedLine delay={0.65}>
                        <em className="text-emerald-400">Clarity,</em> perfected.
                    </MaskedLine>
                </h1>

                <MaskedLine delay={0.85} className="mt-8 max-w-xl">
                    <p
                        data-testid="hero-subheading"
                        className="text-base font-light leading-relaxed text-[#a3b8a8] sm:text-lg"
                    >
                        A dedicated eye-care clinic and optician beside Shakti Sangha Club — where
                        state-of-the-art diagnostics meet genuinely patient-centered care.
                    </p>
                </MaskedLine>

                <FadeUp delay={1.05} className="mt-10 flex flex-wrap items-center gap-4">
                    <a
                        href={CLINIC.phoneHref}
                        data-testid="hero-cta-book"
                        className="group flex items-center gap-3 rounded-full bg-emerald-500 px-8 py-4 text-sm font-semibold text-[#04120a] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_40px_rgba(34,197,94,0.5)]"
                    >
                        <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                        Call to Book
                    </a>
                    <Link
                        to="/remedy"
                        className="group flex items-center gap-2 rounded-full border border-emerald-500/30 px-8 py-4 text-sm font-medium text-emerald-100 backdrop-blur-sm transition-all duration-300 hover:border-emerald-400/70 hover:bg-emerald-500/10"
                    >
                        Explore Our Remedies
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </FadeUp>

                <FadeUp delay={1.2} className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4">
                    <a
                        href={CLINIC.googleProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-rating-badge"
                        className="flex items-center gap-2.5 text-sm text-emerald-100/80 transition-colors duration-300 hover:text-emerald-200"
                    >
                        <Stars />
                        <strong className="font-semibold text-emerald-50">{CLINIC.rating}</strong>
                        on Google
                    </a>
                    <span
                        data-testid="hero-location-badge"
                        className="flex items-center gap-2 text-sm text-[#a3b8a8]"
                    >
                        <MapPin className="h-4 w-4 text-emerald-500" />
                        {CLINIC.addressShort}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-[#a3b8a8]">
                        <Clock className="h-4 w-4 text-emerald-500" />
                        Open today · 9 AM – 10 PM
                    </span>
                </FadeUp>
            </motion.div>
        </section>
    );
};

const BENTO = [
    {
        icon: ShieldCheck,
        title: "Expertise & Experience",
        desc: "Services provided by highly skilled eye care professionals, utilizing the latest advanced equipment and state-of-the-art technology.",
        span: "lg:col-span-7",
        testId: "bento-card-expertise",
    },
    {
        icon: HeartHandshake,
        title: "Patient-Centered Care",
        desc: "Every patient is unique; we assess the patient's condition and provide the necessary treatment and care tailored to their specific needs.",
        span: "lg:col-span-5",
        testId: "bento-card-patient-care",
    },
    {
        icon: MapPin,
        title: "Easy to Reach",
        desc: "It is within walking distance from the Master-da Surya Sen Metro station (Kolkata), accessible by buses (228, SD5, 80A, S-7, 6, Naktala, Harinavi Mini Buses) or auto-rickshaws (Tollygunge route); get off at the Naktala High School/Shakti Sangha Club stop. Located on Netaji Subhash Chandra Bose Road beside Shakti Sangha Club.",
        span: "lg:col-span-5",
        testId: "bento-card-location",
    },
    {
        icon: Accessibility,
        title: "Welcoming to All",
        desc: "Due to its location on the ground floor, accessibility for physically challenged persons is comfortable.",
        span: "lg:col-span-7",
        testId: "bento-card-access",
    },
];

const WhyUs = () => (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <SectionTag className="mb-6">Why Optiplus</SectionTag>
        <FadeUp>
            <h2 className="font-display max-w-3xl text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
                Care that begins with <em className="text-emerald-400">listening</em>
            </h2>
        </FadeUp>
        <div data-testid="bento-why-us-grid" className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-12">
            {BENTO.map((c, i) => (
                <FadeUp key={c.testId} delay={i * 0.08} className={c.span}>
                    <div
                        data-testid={c.testId}
                        className="group relative h-full overflow-hidden rounded-2xl border border-emerald-500/15 bg-[#101f15]/70 p-8 transition-all duration-500 hover:border-emerald-400/50 hover:bg-[#14261a] hover:shadow-[0_0_36px_rgba(34,197,94,0.12)] lg:p-10"
                    >
                        <c.icon className="mb-6 h-8 w-8 text-emerald-400 transition-transform duration-500 group-hover:scale-110" />
                        <h3 className="font-display mb-3 text-2xl font-normal tracking-tight">{c.title}</h3>
                        <p className="text-sm font-light leading-relaxed text-[#a3b8a8]">{c.desc}</p>
                    </div>
                </FadeUp>
            ))}
        </div>
    </section>
);

const SERVICES_TEASER = [
    { ...SERVICES[0], img: IMAGES.exam, testId: "service-card-exam" },
    { ...SERVICES[1], img: IMAGES.frames, testId: "service-card-lenses" },
    { ...SERVICES[3], img: IMAGES.consult, testId: "service-card-surgery" },
];

const ServicesTeaser = () => (
    <section className="border-y border-emerald-500/10 bg-[#0b150e]">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <SectionTag className="mb-6">What we treat</SectionTag>
                    <FadeUp>
                        <h2 className="font-display max-w-2xl text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
                            Clinical care, from first exam to surgery
                        </h2>
                    </FadeUp>
                </div>
                <FadeUp delay={0.1}>
                    <Link
                        to="/remedy"
                        className="group flex items-center gap-2 text-sm font-medium text-emerald-300 transition-colors hover:text-emerald-200"
                    >
                        View all remedies
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </FadeUp>
            </div>

            <div data-testid="services-teaser-list" className="mt-14 grid gap-6 md:grid-cols-3">
                {SERVICES_TEASER.map((s, i) => (
                    <FadeUp key={s.testId} delay={i * 0.1}>
                        <Link to="/remedy" data-testid={s.testId} className="group block">
                            <div className="relative overflow-hidden rounded-2xl border border-emerald-500/15">
                                <img
                                    src={s.img}
                                    alt={s.title}
                                    className="emerald-duotone aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#060a07] via-transparent to-transparent" />
                                <div className="absolute inset-0 bg-emerald-950/25 mix-blend-multiply" />
                                <div className="absolute bottom-0 p-6">
                                    <s.icon className="mb-3 h-6 w-6 text-emerald-400" />
                                    <h3 className="font-display text-2xl font-normal tracking-tight">{s.title}</h3>
                                </div>
                            </div>
                            <p className="mt-4 text-sm font-light leading-relaxed text-[#a3b8a8] transition-colors duration-300 group-hover:text-emerald-100/80">
                                {s.desc}
                            </p>
                        </Link>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

const Storefront = () => (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="relative lg:col-span-7">
                <FadeUp>
                    <div className="grid grid-cols-2 items-start gap-4 sm:gap-6">
    <div className="group relative mt-12 overflow-hidden rounded-2xl border border-emerald-500/20">
        <img
            src={storefront1}
            alt="Optiplus illuminated storefront at night, Naktala"
            className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
    </div>

    <div className="flex flex-col gap-4 sm:gap-6">
        <div className="group relative overflow-hidden rounded-2xl border border-emerald-500/20">
            <img
                src={storefront2}
                alt="Optiplus eyewear display wall — frames collection"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
        </div>

        <div className="group relative overflow-hidden rounded-2xl border border-emerald-500/20">
            <img
                src={storefront3}
                alt="Optiplus eyewear display wall — frames collection"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
        </div>
    </div>
</div>
                </FadeUp>
                <FadeUp delay={0.15}>
                    <p className="mt-5 text-center text-xs font-mono tracking-[0.22em] uppercase text-[#687f6e]">
                        Our store &amp; frame collection · beside Shakti Sangha Club, Naktala
                    </p>
                </FadeUp>
            </div>
            <div className="lg:col-span-5">
                <SectionTag className="mb-6">Step into our store</SectionTag>
                <FadeUp>
                    <h2 className="font-display text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
                        Scientific comprehensive eye examination, <em className="text-emerald-400">accuracy & economy</em>
                    </h2>
                    <p className="mt-5 text-base font-light leading-relaxed text-[#a3b8a8]">
                        That's the promise painted on our board — and practiced inside, every day
                        since 1990.
                    </p>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <div
                        data-testid="specialist-hours-card"
                        className="mt-10 rounded-2xl border border-emerald-500/20 bg-[#101f15]/70 p-8 transition-all duration-500 hover:border-emerald-400/50"
                    >
                        <p className="text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">
                            Eye Specialists attend daily
                        </p>
                        <div className="mt-5 flex items-start gap-4">
                            <Clock className="mt-1 h-6 w-6 shrink-0 text-emerald-400" />
                            <p className="text-base font-light leading-relaxed text-[#a3b8a8]">
                                From 11:00 a.m. to 2:00 p.m. &amp; from 7:00 p.m. to 9:00 p.m.
                            </p>
                        </div>
                    </div>
                </FadeUp>
            </div>
        </div>
    </section>
);

const Testimonials = () => (
    <section data-testid="testimonials-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <SectionTag className="mb-6">Patient voices</SectionTag>
        <FadeUp>
            <h2 className="font-display max-w-3xl text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
                Trusted by South Kolkata
            </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
            <FadeUp className="lg:col-span-4">
                <a
                    href={CLINIC.googleProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-rating-card"
                    className="flex h-full flex-col justify-center rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-[#101f15] to-[#0b150e] p-10 text-center transition-all duration-500 hover:border-emerald-400/50 hover:shadow-[0_0_36px_rgba(34,197,94,0.12)]"
                >
                    <Star className="mx-auto mb-4 h-8 w-8 fill-amber-400 text-amber-400" />
                    <p className="font-display text-7xl font-light text-emerald-50">{CLINIC.rating}</p>
                    <Stars className="mt-3 h-5 w-5" />
                    <p className="mt-4 text-sm text-[#a3b8a8]">Google reviews</p>
                    <p className="font-display mt-5 border-t border-emerald-500/10 pt-5 text-base font-light italic leading-relaxed text-emerald-50/80">
                        “{GOOGLE_REVIEW.quote}”
                    </p>
                    <p className="mt-2 text-[11px] font-mono tracking-[0.18em] uppercase text-[#687f6e]">
                        {GOOGLE_REVIEW.source}
                    </p>
                    <p className="mt-1 text-xs font-mono tracking-[0.2em] uppercase text-emerald-500">
                        Rated eye-care center
                    </p>
                    <p className="mt-4 text-[11px] font-mono tracking-[0.18em] uppercase text-emerald-400/70">
                        Read all reviews ↗
                    </p>
                </a>
            </FadeUp>
            {TESTIMONIALS.slice(0, 2).map((t, i) => (
                <FadeUp key={t.testId} delay={0.1 + i * 0.1} className="lg:col-span-4">
                    <figure
                        data-testid={t.testId}
                        className="flex h-full flex-col justify-between rounded-3xl border border-emerald-500/15 bg-[#101f15]/70 p-8 transition-all duration-500 hover:border-emerald-400/40 lg:p-10"
                    >
                        <blockquote className="font-display text-xl font-light italic leading-relaxed text-emerald-50/90">
                            “{t.quote}”
                        </blockquote>
                        <figcaption className="mt-8 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-emerald-50">{t.name}</p>
                                <p className="text-xs text-[#687f6e]">{t.area} · {t.service}</p>
                            </div>
                            <Stars className="h-3.5 w-3.5" />
                        </figcaption>
                    </figure>
                </FadeUp>
            ))}
        </div>
    </section>
);

const HoursStrip = () => (
    <section data-testid="home-hours-strip" className="border-t border-emerald-500/10 bg-[#0b150e]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-8 md:grid-cols-3 lg:px-16 lg:py-20">
            <FadeUp className="flex gap-4">
                <MapPin className="h-6 w-6 shrink-0 text-emerald-400" />
                <div>
                    <p className="mb-1 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Find us</p>
                    <p data-testid="home-address-link" className="text-sm font-light leading-relaxed text-[#a3b8a8]">
                        {CLINIC.address}
                    </p>
                </div>
            </FadeUp>
            <FadeUp delay={0.1} className="flex gap-4">
                <Clock className="h-6 w-6 shrink-0 text-emerald-400" />
                <div>
                    <p className="mb-1 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Hours</p>
                    <p className="text-sm font-light text-[#a3b8a8]">{CLINIC.hours}</p>
                    <p className="text-sm font-light text-[#687f6e]">{CLINIC.sunday}</p>
                </div>
            </FadeUp>
            <FadeUp delay={0.2} className="flex gap-4">
                <Phone className="h-6 w-6 shrink-0 text-emerald-400" />
                <div>
                    <p className="mb-1 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Call us</p>
                    <a
                        href={CLINIC.phoneHref}
                        data-testid="home-phone-link"
                        className="text-3xl font-light text-emerald-50 transition-colors hover:text-emerald-300"
                    >
                        {CLINIC.phoneDisplay}
                    </a>
                </div>
            </FadeUp>
        </div>
    </section>
);

const Home = () => (
    <main data-testid="home-page">
        <Hero />
        <Marquee />
        <WhyUs />
        <Storefront />
        <ServicesTeaser />
        <Testimonials />
        <HoursStrip />
    </main>
);

export default Home;